import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { createHttpApiClient } from "./api/client";
import { API_URL, GAME_SERVER_URL } from "./config";
import { routeAppOpen } from "./boot/open-route";
import { claimAppBootstrap } from "./boot/single-instance";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { setCardFacesFailureSink } from "./game/card-faces";
import { setDeckFailureSink } from "./game/deck-assets";
import { crashReturn } from "./game/crash-return";
import { reportClientFault } from "./game/client-report";
import { installGlobalErrorRouting, lifecycle } from "./game/lifecycle";
import { watchStyles } from "./game/style-sentinel";
import { resumeAudioOnForeground, setSfxActivitySource } from "./sound/sfx";
import { createColyseusPresenceConnector } from "./game/presence";
import { SessionProvider } from "./store/context";
import { createSessionStore } from "./store/session";
import { setTelegramColorScheme } from "./settings/prefs";
import { setupInputAutoScroll } from "./telegram/keyboard-scroll";
import { resolveLaunchContext } from "./telegram/launch";
import { setPlatformFor3D } from "./game/supports-3d";
import { seedSafeAreaTop, setupViewport } from "./telegram/viewport";
import { startOrientationLock } from "./telegram/orientation";
import { startBoardHeightFreeze } from "./telegram/board-height";
import { ConnectionOverlay } from "./components/ConnectionOverlay";
import { FriendToast } from "./components/FriendToast";
import { GameSwitchOverlay } from "./components/GameSwitchOverlay";
import { UprightScreen } from "./components/UprightScreen";
import { InvitePrompt } from "./components/InvitePrompt";
import { ShareInvite } from "./screens/ShareInvite";
import { ViewportProbe, viewportProbeEnabled } from "./dev/ViewportProbe";
import "@fontsource/vazirmatn/400.css";
import "@fontsource/vazirmatn/600.css";
import "@fontsource/vazirmatn/700.css";
import "@fontsource/vazirmatn/800.css";
import "./styles/tailwind.css";
import "./styles/theme.css";

function bootstrap(): void {
  // BEFORE ANYTHING PAINTS. Telegram reports the device's top inset over the JS bridge, which
  // answers long after the first screen is up — so the shell's top padding goes 0 → ~80px mid-
  // entry and every screen visibly drops. This re-applies the last known value synchronously; the
  // bridge still overwrites it with the truth a moment later. See `seedSafeAreaTop`.
  seedSafeAreaTop();
  const launch = resolveLaunchContext();
  // STAGING-ONLY viewport diagnostic: three headless sweeps disagreed with two real phones about
  // whether the keyboard resizes the board, so the numbers now come off the device. Hostname-gated —
  // prod (info.limoozin.info / game.limoozin.info / top. / app.binggames.top) can never match.
  const viewportProbeOn = viewportProbeEnabled(location.hostname, location.search);
  // Follow only Telegram's light/dark *scheme* — the Bing palettes are a
  // deliberate brand (gold on felt / Royal Mint light), so we keep our design
  // tokens rather than let Telegram's per-color params override them. The
  // «پوسته» preference (settings/prefs.ts) resolves auto/dark/light and stamps
  // `data-theme` on <html>.
  setTelegramColorScheme(launch.colorScheme);

  // Open the Mini App full-screen ON MOBILE (ios/android — desktop keeps its default
  // medium window; forcing fullscreen there stretched the layout), bind layout height
  // to Telegram's viewport (--tg-viewport-* via bindCssVars), and stop swipe-down from
  // collapsing it. No-ops cleanly outside Telegram. See telegram/viewport.ts.
  void setupViewport(launch.platform);
  // PORTRAIT ON PHONES (card-f5 item 3): Telegram's orientation lock while a phone is upright, the
  // «turn your phone upright» screen (below) while it is sideways. Desktop and web: nothing at all.
  // After setupViewport (its SDK init() has run); before the first render. See telegram/orientation.ts.
  startOrientationLock({ platform: launch.platform, version: launch.version });
  // Tell the dice renderer which Telegram platform we are on BEFORE anything draws: Android's
  // WebView reports 3-D support and then composites it flat, so it cannot be detected.
  setPlatformFor3D(launch.platform);

  // Scroll a focused text field into view above the on-screen keyboard (profile fields
  // etc.) — the WebView doesn't do it reliably inside our custom scroll container.
  setupInputAutoScroll();

  // BOARD HEIGHT: hold --game-h at the height last measured with NOTHING focused, so the game tables
  // do not re-fit themselves when the chat keyboard opens. Keyed on FOCUS, not on any height signal:
  // measured on two real devices, Telegram's stable height AND every viewport unit shrink with the
  // keyboard, and `data-keyboard` never fires. Called here rather than from setupViewport() because
  // it needs no Telegram bridge — a stalled viewport.mount() must never keep it from starting.
  startBoardHeightFreeze();

  // On-screen debug console (eruda) — enable with VITE_DEBUG=1. Logs the exact
  // initData so auth ("bad signature") issues are visible on the device.
  if (import.meta.env.VITE_DEBUG === "1") {
    void import("eruda").then(({ default: eruda }) => {
      eruda.init();
      console.log("[debug] API_URL =", API_URL);
      console.log("[debug] inTelegram =", launch.inTelegram);
      console.log("[debug] initDataRaw =", launch.initDataRaw);
      if (launch.initDataRaw) {
        const params = new URLSearchParams(launch.initDataRaw);
        console.log("[debug] fields =", [...params.keys()].sort().join(", "));
        console.log(
          "[debug] has signature =",
          params.has("signature"),
          "| has hash =",
          params.has("hash"),
        );
        console.log("[debug] auth_date =", params.get("auth_date"));
      }
    });
  }

  // The browser's IANA timezone (stable for the session); resolved once, guarded.
  const clientTimeZone = ((): string | null => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      return null;
    }
  })();
  // The store is built below (it needs `api`), so the maintenance hook is late-bound — the same
  // shape as `installGlobalErrorRouting`'s `isGameActive` further down.
  let sessionStore: ReturnType<typeof createSessionStore> | null = null;

  const api = createHttpApiClient({
    baseUrl: API_URL,
    getInitData: () => launch.initDataRaw,
    // MAINTENANCE: while the platform is being updated the server refuses every request with
    // 503 `MAINTENANCE` (admins are exempt, checked server-side). Mirror it into the store so
    // the app can render the maintenance screen. It clears on the next SUCCESSFUL response, so
    // the app comes back by itself when maintenance ends — no reload, no user action.
    onMaintenance: (state) => sessionStore?.getState().setMaintenance(state),
    // 403 `CHANNELS_REQUIRED` — the user must join the mandatory channels first. Mirror it into the
    // store so the app renders the join gate; it clears on the next successful response (after «عضو
    // شدم» once they've joined).
    onChannelGate: (channels) => sessionStore?.getState().setChannelGate(channels),
    // Client-capture context for the api's analytics (platform/version from the
    // Telegram launch params, timezone from the browser). All best-effort/nullable.
    getClientContext: () => ({
      platform: launch.platform,
      version: launch.version,
      timezone: clientTimeZone,
    }),
  });
  // Background presence/friend-realtime socket (connected once the session is ready
  // in App.tsx). Injected so tests can drive it with a fake connector.
  const presenceConnector = createColyseusPresenceConnector({
    wsUrl: GAME_SERVER_URL,
    getInitData: () => launch.initDataRaw,
  });
  const store = createSessionStore({ api, initDataRaw: launch.initDataRaw, presenceConnector });
  sessionStore = store; // late-bind the maintenance hook above

  // Mobile lifecycle: fuse visibility/pageshow/freeze/Telegram-activity into ONE
  // signal, suspend the registered sockets on hidden, and health-probe + reconnect
  // them on resume (screen lock kills the WS; iOS leaves half-open sockets). The
  // connectors register themselves; the ConnectionOverlay below renders the state.
  // The sound layer skips playback while the app is off screen (iOS refuses to start the audio
  // device there and the rejection escapes Howler asynchronously). Hand it the SAME fused activity
  // signal the reconnect lifecycle uses — `document.visibilityState` alone misses the Telegram
  // WebView's own active/inactive transitions.
  setSfxActivitySource(() => lifecycle.appActive);
  // …and WAKE the audio when the app comes back. iOS INTERRUPTS the WebAudio context on a screen
  // lock, and an interrupted context never plays again until something resumes it — «lock the phone
  // once, come back, and every game is silent for the rest of the session». The pointerdown handler
  // in `sound/sfx.ts` covers the next tap; this covers coming back to a board that is already
  // playing itself. Guarded end to end: no audio failure may reach the crash router below.
  resumeAudioOnForeground((cb) => lifecycle.subscribe((s) => cb(s.appActive)));
  lifecycle.start();
  // Route uncaught errors/rejections into the overlay while a game is on screen —
  // instead of Telegram's WebView "unexpected error" crash sheet. Always logged.
  installGlobalErrorRouting({ isGameActive: () => store.getState().view === "game" });

  // Did the app actually PAINT? Nothing checked, and a half-rendered table reports nothing on its
  // own — see game/style-sentinel.ts.
  watchStyles();

  // A lazy CHUNK that gives up says so. It cannot report itself: a failed `import()` is a rejected
  // promise, so the capture-phase resource listener above never sees it, and every caller catches —
  // which is why a severed artwork chunk left the player on a recovery screen and us with nothing.
  // The loader stays dependency-free and is handed the beacon here, beside the other telemetry.
  setCardFacesFailureSink((failure) => reportClientFault("chunk-failed", { ...failure }));
  // An image deck whose face spent every attempt (`game/deck-assets.ts`). Its preloads are detached
  // `Image` objects, which the capture-phase resource listener in `lifecycle.ts` cannot see — so the
  // loader reports the terminal failure itself, as the same `resource-failed` an `<img>` would.
  setDeckFailureSink((failure) =>
    reportClientFault("resource-failed", {
      tag: "img",
      url: failure.url,
      deck: failure.deck,
      attempts: failure.attempts,
    }),
  );

  // «دعوت دوستان»: the bot's invite button opens the app with
  // `?bing_action=share-invite`. That mode renders ONLY the ShareInvite screen —
  // it must not wait on the boot chain (profile/channel-gate/sockets), which is
  // what used to swallow the share and drop the user on the lobby.
  const shareInviteMode =
    new URLSearchParams(window.location.search).get("bing_action") === "share-invite";

  // WHERE THE APP OPENS, before the first render: a `t_<game>_<roomId>` deep link opens straight into
  // that table (joining by id instead of the lobby) — and a PUBLIC seat this device still holds wins over
  // everything, the link included: straight back to it (card-a5 item 1). See `boot/open-route.ts`.
  routeAppOpen(store, launch.startParam, shareInviteMode);

  const rootEl = document.getElementById("root");
  if (!rootEl) throw new Error("Missing #root element");

  createRoot(rootEl).render(
    <StrictMode>
      {/* A render throw used to unmount the whole tree and leave a blank page — React rethrows to
          window.onerror so it was seen, but only AFTER everything was gone and with no idea which
          subtree failed. The boundary reports the component stack and shows a recoverable screen.
          A crash at a table is a LOST CONNECTION (the seat keeps its grace), and the screen offers
          the way back to it: this device's ticket, as the maintenance screen does (card-a5 item 3).
          Not in the share-invite mode, which never shows a table. */}
      <ErrorBoundary tableReturn={shareInviteMode ? undefined : () => crashReturn(store)}>
        {shareInviteMode ? (
          <ShareInvite api={api} />
        ) : (
          <SessionProvider store={store}>
            <App />
            <FriendToast />
            {/* Incoming game-invite prompt: overlays any view, and sweeps
              GET /invites/incoming on open for invites missed while offline. */}
            <InvitePrompt />
            {/* Reconnecting / failed / seat-expired connection states over a live table. */}
            <ConnectionOverlay />
            {/* «در حال خروج از بازی فعلی…» — accept-invite-while-in-a-game switch transition. */}
            <GameSwitchOverlay />
            {/* STAGING-ONLY viewport diagnostic (see dev/ViewportProbe). Gated on the hostname, so it
              can never render on prod; the import stays static so the gate is the ONLY thing that
              decides, rather than a lazy chunk that might load late and miss the keyboard event. */}
            {viewportProbeOn ? <ViewportProbe /> : null}
          </SessionProvider>
        )}
        {/* «Turn your phone upright» over a sideways phone, in both modes. BESIDE the tree, never
            around it: it covers a live table, it never unmounts it. */}
        <UprightScreen />
      </ErrorBoundary>
    </StrictMode>,
  );
}

// ★ ONE APP PER DOCUMENT. A web recreate renames the lazy chunks, so a page that is mid-session
// at the deploy 404s its next `import()`; the card-faces retry then pulls the NEW entry chunk in
// beside the old one and EVERYTHING below would run a second time — two React roots on one
// #root, which is the `removeChild` crash. See boot/single-instance.ts. Everything with a side
// effect lives inside bootstrap() so this ONE line decides whether any of it runs.
if (claimAppBootstrap()) bootstrap();
